import { AxleListing } from '../axle/AxleListing';
import { TractorWithAxlesResponse } from '../tractor/TractorWithAxlesResponse';
import { TrailerWithAxlesResponse } from '../trailer/TrailerWithAxlesResponse';
import { EntryValue } from './EditOrderRequest';
import {
  AxleFieldNames,
  AxleFieldValues,
  AxleFormName,
} from './fields/AxleInformationFields';
import {
  TractorAxlesFormName,
  TractorFieldNames,
  TractorFields,
} from './fields/TractorInformationFields';
import {
  TrailerFieldNames,
  TrailerFields,
} from './fields/TrailerInformationFields';
import { normalizeAxleType } from './valueNormalizers/AxleType';
import { normalizePlateType } from './valueNormalizers/PlateType';
import { normalizeSuspensionType } from './valueNormalizers/SuspensionType';
import { normalizeTireSize } from './valueNormalizers/TireSize';
import { normalizeTractorBodyStyle } from './valueNormalizers/TractorBodyStyle';
import { normalizeTractorFuelType } from './valueNormalizers/TractorFuelType';
import { normalizeTractorType } from './valueNormalizers/TractorType';
import { normalizeTrailerType } from './valueNormalizers/TrailerType';

export const AxleAssembler = {
  toFormData: (axle: AxleListing) => {
    return {
      [AxleFieldNames.AxleId]: axle.id,
      [AxleFieldNames.AxleWeight]: axle.weight,
      [AxleFieldNames.AxleCapacity]: axle.capacity,
      [AxleFieldNames.AxleWidth]: axle.width,
      [AxleFieldNames.TireCount]: axle.tireCount,
      [AxleFieldNames.TireRating]: axle.tireRating,
      [AxleFieldNames.TireSize]: normalizeTireSize(axle.tireSize),
      [AxleFieldNames.SuspensionType]: normalizeSuspensionType(
        axle.suspensionType,
      ),
      [AxleFieldNames.AxleGroup]: axle.group,
      [AxleFieldNames.IsSteeringAxle]: axle.isSteeringAxle ?? false,
      [AxleFieldNames.AxleSpacing]: axle.spacing,
      [AxleFieldNames.AxleType]: normalizeAxleType(axle.type),
    };
  },
};
export type AxleForm = ReturnType<typeof AxleAssembler.toFormData>;

export const TrailerFormName = 'trailers';
export const TripsFormName = 'trips';

export const TrailerAssembler = {
  toFormData: (trailer: TrailerWithAxlesResponse) => {
    return {
      [TrailerFieldNames.TrailerUnitNumber]: trailer.unitNumber,
      [TrailerFieldNames.TrailerMake]: trailer.make,
      [TrailerFieldNames.TrailerType]: normalizeTrailerType(trailer.type),
      [TrailerFieldNames.TrailerVIN]: trailer.vin,
      [TrailerFieldNames.TrailerYear]: trailer.year,
      [TrailerFieldNames.TrailerAxleCount]: trailer.axles.length.toString(),
      [TrailerFieldNames.TrailerLicensePlate]: trailer.licensePlateNumber,
      [TrailerFieldNames.TrailerLicensePlateState]: trailer.licensePlateState,
      [TrailerFieldNames.TrailerLicensePlateType]: normalizePlateType(
        trailer.licensePlateType,
      ),
      [TrailerFieldNames.TrailerLength]: trailer.length ?? undefined,
      [TrailerFieldNames.TrailerWidth]: trailer.width,
      [TrailerFieldNames.TrailerHeight]: trailer.height,
      [TrailerFieldNames.TrailerKingpinSetbackLength]:
        trailer.kingpinSetbackLength,
      [TrailerFieldNames.TrailerKingpinSettingLength]:
        trailer.kingpinSettingLength,
      [TrailerFieldNames.TrailerWheelbaseLength]: trailer.wheelbaseLength,
      [TrailerFieldNames.TrailerEmptyWeight]: trailer.emptyWeight,
      [TrailerFieldNames.TrailerGroundClearance]: trailer.groundClearance,
      [TrailerFieldNames.TrailerDeckHeight]: trailer.deckHeight,
      [TrailerFieldNames.TrailerRegisteredWeight]: trailer.registeredWeight,
      axles: trailer.axles.map(AxleAssembler.toFormData),
    };
  },
};

export const TractorAssembler = {
  toFormData: (tractor: TractorWithAxlesResponse) => {
    return {
      [TractorFieldNames.TractorUnitNumber]: tractor.unitNumber,
      [TractorFieldNames.TractorMake]: tractor.make,
      [TractorFieldNames.TractorVIN]: tractor.vin,
      [TractorFieldNames.TractorYear]: tractor.year,
      [TractorFieldNames.TractorAxleCount]: tractor.axles.length.toString(),
      [TractorFieldNames.TractorLicensePlateNumber]: tractor.licensePlate,
      [TractorFieldNames.TractorLicensePlateState]: tractor.licensePlateState,
      [TractorFieldNames.TractorModelNumber]: tractor.model,
      [TractorFieldNames.TractorType]: normalizeTractorType(tractor.type),
      [TractorFieldNames.TractorFuelType]: normalizeTractorFuelType(
        tractor.fuelType,
      ),
      [TractorFieldNames.TractorBodyStyle]: normalizeTractorBodyStyle(
        tractor.bodyStyle,
      ),
      [TractorFieldNames.VehicleOfSpecialDesign]: tractor.specialDesign,
      [TractorFieldNames.TractorLicenseEffectiveDate]:
        tractor.licensePlateEffectiveDate,
      [TractorFieldNames.TractorLicenseExpirationDate]:
        tractor.licensePlateExpirationDate,
      [TractorFieldNames.TractorLicensePlateType]: normalizePlateType(
        tractor.licensePlateType,
      ),
      [TractorFieldNames.TractorLicensePlateYear]: tractor.licensePlateYear,
      [TractorFieldNames.TractorLicenseIsApportionedPlate]:
        tractor.licensePlateIsApportioned,
      [TractorFieldNames.TractorLength]: tractor.length,
      [TractorFieldNames.TractorWidth]: tractor.width,
      [TractorFieldNames.TractorHeight]: tractor.height,
      [TractorFieldNames.TractorWheelBase]: tractor.wheelBase,
      [TractorFieldNames.TractorGroundHeight]: tractor.groundHeight,
      [TractorFieldNames.TractorUnladenWeight]: tractor.unladenWeight,
      [TractorFieldNames.TractorActualWeight]: tractor.actualWeight,
      [TractorFieldNames.TractorGVW]: tractor.gvw,
      [TractorFieldNames.TractorOuterBridgeLength]: tractor.outerBridgeLength,
      tractorAxles: {
        axles: tractor.axles.map(AxleAssembler.toFormData),
      },
    };
  },
};

export function mergeFormWithTractor(
  form: Record<string, unknown>,
  fields: string[],
  tractor: TractorWithAxlesResponse,
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
): Record<string, unknown> {
  const axleFields = getCurrentAxleFields(fields);
  if (axleFields.length === 0) {
    return prefillMissingTractorValues(form, tractor, fields, merge);
  }

  if (!alreadyContainsTractorAxles(form)) {
    return {
      ...prefillMissingTractorValues(form, tractor, fields, merge),
      ...prefillAllAxleValuesFromTractor(tractor, axleFields),
    };
  }

  return {
    ...prefillMissingTractorValues(form, tractor, fields, merge),
    ...prefillMissingAxleValuesFromTractor(form, tractor, axleFields, merge),
  };
}

const prefillMissingAxleValuesFromTractor = (
  form: Record<string, unknown>,
  selectedTractor: TractorWithAxlesResponse,
  axleFields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  return {
    [TractorAxlesFormName]: {
      [AxleFormName]: prefillMissingAxleValuesFromUnit(
        (form[TractorAxlesFormName] as Record<string, unknown> | undefined)?.[
          AxleFormName
        ] as unknown as Record<string, EntryValue>[],
        selectedTractor.axles,
        axleFields,
        merge,
      ),
    },
  };
};

const prefillMissingTrailerValues = (
  trailerForm: Record<string, unknown>,
  matchingTrailer: TrailerWithAxlesResponse,
  currentTrailerFields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  const defaultTrailerValues = TrailerAssembler.toFormData(matchingTrailer);
  const defaultRequiredValues = keepOnlySelectedEntries({
    entries: defaultTrailerValues,
    selectedKeys: currentTrailerFields,
  });

  return merge({}, defaultRequiredValues, trailerForm);
};

const getCurrentTrailerFields = (currentFields: string[]) =>
  currentFields.filter((field) => TrailerFields.includes(field));

export function mergeFormWithSelectedTrailers(
  form: Record<string, unknown>,
  fields: string[],
  trailers: TrailerWithAxlesResponse[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) {
  const trailersForm = form[TrailerFormName];
  const noTrailerSelected = !Array.isArray(trailersForm);

  if (noTrailerSelected) return form;

  const currentTrailerFields = getCurrentTrailerFields(fields);
  const currentAxleFields = getCurrentAxleFields(fields);

  const updatedTrailers = trailersForm.map((trailerForm) =>
    mergeFormWithTrailer(
      trailerForm,
      trailers,
      currentTrailerFields,
      currentAxleFields,
      merge,
    ),
  );

  return {
    ...form,
    [TrailerFormName]: updatedTrailers,
  };
}

const mergeFormWithTrailer = (
  trailerForm: Record<string, EntryValue>,
  trailers: TrailerWithAxlesResponse[],
  trailerFields: string[],
  axleFields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  const matchingTrailer = trailers.find(
    (trailer) => trailer.id === trailerForm.trailerId,
  );

  if (!matchingTrailer) {
    return trailerForm;
  }

  if (axleFields.length === 0) {
    return prefillMissingTrailerValues(
      trailerForm,
      matchingTrailer,
      trailerFields,
      merge,
    );
  }

  if (!alreadyContainsTrailerAxles(trailerForm)) {
    return {
      ...prefillMissingTrailerValues(
        trailerForm,
        matchingTrailer,
        trailerFields,
        merge,
      ),
      ...prefillAllAxleValuesFromTrailer(matchingTrailer, axleFields),
    };
  }

  return {
    ...prefillMissingTrailerValues(
      trailerForm,
      matchingTrailer,
      trailerFields,
      merge,
    ),
    ...prefillMissingAxleValuesFromTrailer(
      trailerForm,
      matchingTrailer,
      axleFields,
      merge,
    ),
  };
};

const prefillMissingAxleValuesFromTrailer = (
  form: Record<string, EntryValue>,
  selectedTrailer: TrailerWithAxlesResponse,
  axleFields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  return {
    [AxleFormName]: prefillMissingAxleValuesFromUnit(
      form[AxleFormName] as Record<string, EntryValue>[],
      selectedTrailer.axles,
      axleFields,
      merge,
    ),
  };
};

const prefillAllAxleValuesFromTrailer = (
  selectedTrailer: TrailerWithAxlesResponse,
  axleFields: string[],
) => ({
  [AxleFormName]: prefillAllAxleValuesFromUnit(
    selectedTrailer.axles,
    axleFields,
  ),
});

const alreadyContainsTrailerAxles = (
  trailerForm: Record<string, EntryValue>,
) => {
  const axlesForm = trailerForm[AxleFormName];
  return Array.isArray(axlesForm) && axlesForm.length > 0;
};

const prefillMissingAxleValuesFromUnit = (
  formAxles: Record<string, EntryValue>[],
  unitAxles: AxleListing[],
  axleFields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  const prefilledAxles: Record<string, unknown>[] = [];

  formAxles.forEach((formAxle) => {
    const formAxleId = formAxle[AxleFieldNames.AxleId];
    const tractorAxle = unitAxles.find(
      (tractorAxle) => tractorAxle.id === formAxleId,
    );

    const defaultAxlesValues = tractorAxle
      ? keepOnlySelectedEntries({
          entries: AxleAssembler.toFormData(tractorAxle),
          selectedKeys: axleFields,
        })
      : {};
    const prefilledAxle = merge({}, defaultAxlesValues, formAxle);
    prefilledAxles.push(prefilledAxle);
  });

  return prefilledAxles;
};

const alreadyContainsTractorAxles = (entries: Record<string, unknown>) =>
  !!(
    entries[TractorAxlesFormName] &&
    (
      (entries[TractorAxlesFormName] as Record<string, unknown>)[
        AxleFormName
      ] as Record<string, unknown> | undefined
    )?.[0]
  );

const prefillAllAxleValuesFromTractor = (
  selectedTractor: TractorWithAxlesResponse,
  axleFields: string[],
) => ({
  [TractorAxlesFormName]: {
    [AxleFormName]: prefillAllAxleValuesFromUnit(
      selectedTractor.axles,
      axleFields,
    ),
  },
});

const prefillAllAxleValuesFromUnit = (
  unitAxles: AxleListing[],
  axleFields: string[],
) =>
  unitAxles.map((axle) =>
    keepOnlySelectedEntries({
      entries: AxleAssembler.toFormData(axle),
      selectedKeys: [...axleFields, AxleFieldNames.AxleId],
    }),
  );

const getCurrentAxleFields = (currentFields: string[]) =>
  currentFields.filter((field) => AxleFieldValues.includes(field));

const getCurrentTractorFields = (currentFields: string[]) =>
  currentFields.filter((field) => TractorFields.includes(field));

const prefillMissingTractorValues = (
  form: Record<string, unknown>,
  selectedTractor: TractorWithAxlesResponse,
  fields: string[],
  merge: (...sources: Record<string, unknown>[]) => Record<string, unknown>,
) => {
  const defaultTractorValues = keepOnlySelectedEntries({
    entries: TractorAssembler.toFormData(selectedTractor),
    selectedKeys: getCurrentTractorFields(fields),
  });
  return merge({}, defaultTractorValues, form);
};

const keepOnlySelectedEntries = ({
  entries,
  selectedKeys,
}: {
  entries: Record<string, unknown>;
  selectedKeys: string[];
}) =>
  Object.keys(entries).reduce(
    (requiredValues: Record<string, unknown>, key) => {
      if (selectedKeys.includes(key)) {
        requiredValues[key] = entries[key];
      }
      return requiredValues;
    },
    {},
  );
